import { useCallback, useRef, useState } from 'react';

import { useBreakPoints, useFormListValidator } from 'core';
import { useDeleteUserImage, useUserData } from 'core/Api';
import { convertToInitials } from 'core/Utils';
import {
  ActionIcon,
  Avatar,
  ButtonMantine,
  FileButton,
  Group,
  ImageCropperModal,
  notifications,
} from 'ui';
import { useForm } from 'ui/Form';
import { IconDelete } from 'ui/Icons';

import { ProfileImageFormType } from './editProfile.types';

const EditProfileImage = ({ logoUrl }: { logoUrl?: string }) => {
  const { isSmallScreen } = useBreakPoints();
  const resetRef = useRef<() => void>(null);
  const [preCroppedFile, setPreCroppedFile] = useState<File>();
  const { mutate: deleteImage } = useDeleteUserImage();
  const { data: user } = useUserData();

  const form = useForm<ProfileImageFormType>({ initialValues: { file: null } });
  useFormListValidator({ form, id: 'profileImageForm' });

  const deleteUserImage = useCallback(() => {
    deleteImage(user?.uuid, {
      onSuccess() {
        notifications.show({
          message: 'Successfully deleted your profile image',
          variant: 'success',
        });
      },
    });
  }, [deleteImage, user?.uuid]);

  return (
    <Group align={'flex-end'}>
      <Group style={{ flexGrow: 1 }}>
        <Avatar
          src={form.values.file ? URL.createObjectURL(form.values.file) : logoUrl}
          size={96}
          radius={96}
          color="cobalt"
        >
          {user?.first_name &&
            user?.last_name &&
            convertToInitials(`${user.first_name} ${user.last_name}`)}
        </Avatar>
      </Group>

      <Group gap={'sm'}>
        <FileButton
          resetRef={resetRef}
          onChange={(file) => {
            if (file) {
              setPreCroppedFile(file);
            }
          }}
          accept="image/png,image/jpeg"
        >
          {(props) => (
            <ButtonMantine variant="outline" size={'sm'} {...props}>
              {logoUrl ? 'Change Image' : 'Add Image'}
            </ButtonMantine>
          )}
        </FileButton>

        {logoUrl &&
          (isSmallScreen ? (
            <ActionIcon variant="outline" color="cerise" size="sm" onClick={deleteUserImage}>
              <IconDelete />
            </ActionIcon>
          ) : (
            <ButtonMantine variant="outline" size={'sm'} color="cerise" onClick={deleteUserImage}>
              Delete Image
            </ButtonMantine>
          ))}
      </Group>

      <ImageCropperModal
        opened={!!preCroppedFile}
        dimensions={[96, 96]}
        isCircularCrop={true}
        onCropped={(croppedImage) => {
          form.setFieldValue('file', croppedImage);
          // Closes the modal
          setPreCroppedFile(undefined);
        }}
        imageUrl={preCroppedFile ? URL.createObjectURL(preCroppedFile) : undefined}
        onClose={() => {
          resetRef.current?.();
          setPreCroppedFile(undefined);
        }}
      />
    </Group>
  );
};

export { EditProfileImage };
